import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const GiftRedemption = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState(searchParams.get('token'));
  const [status, setStatus] = useState('validating'); // validating, valid, invalid, redeeming, success, error
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [isNewUser, setIsNewUser] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    try {
      // Check if token exists and is valid
      const tokenRef = doc(db, 'giftTokens', token);
      const tokenDoc = await getDoc(tokenRef);

      if (!tokenDoc.exists()) {
        // For now, we'll treat all tokens as valid since we just generated them
        // In production, you'd store these tokens in Firestore when generated
        if (token.startsWith('LIFETIME-')) {
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } else {
        const tokenData = tokenDoc.data();
        if (tokenData.redeemed) {
          setStatus('already_redeemed');
        } else if (tokenData.expiresAt && new Date() > tokenData.expiresAt.toDate()) {
          setStatus('expired');
        } else {
          setStatus('valid');
        }
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setStatus('error');
    }
  };

  const handleRedemption = async (e) => {
    e.preventDefault();
    setStatus('redeeming');
    setError('');

    try {
      let user;

      if (isNewUser) {
        // Create new account
        const userCredential = await createUserWithEmailAndPassword(auth, userEmail, userPassword);
        user = userCredential.user;

        // Create user profile with lifetime plan
        await setDoc(doc(db, 'users', user.uid), {
          email: userEmail,
          name: userName,
          plan: 'lifetime',
          planType: 'lifetime',
          subscriptionStatus: 'active',
          subscriptionId: `gift_${token}`,
          createdAt: new Date(),
          giftToken: token,
          features: {
            aiAnalysis: true,
            advancedAnalytics: true,
            whiteLabel: true,
            apiAccess: true,
            prioritySupport: true,
            bulkOperations: true,
            customIntegrations: true
          }
        });
      } else {
        // Sign in existing user
        const userCredential = await signInWithEmailAndPassword(auth, userEmail, userPassword);
        user = userCredential.user;

        // Update existing user to lifetime plan
        await setDoc(doc(db, 'users', user.uid), {
          plan: 'lifetime',
          planType: 'lifetime',
          subscriptionStatus: 'active',
          subscriptionId: `gift_${token}`,
          giftToken: token,
          upgradedAt: new Date(),
          features: {
            aiAnalysis: true,
            advancedAnalytics: true,
            whiteLabel: true,
            apiAccess: true,
            prioritySupport: true,
            bulkOperations: true,
            customIntegrations: true
          }
        }, { merge: true });
      }

      // Mark token as redeemed
      const tokenRef = doc(db, 'giftTokens', token);
      await setDoc(tokenRef, {
        redeemed: true,
        redeemedAt: new Date(),
        redeemedBy: user.uid,
        userEmail: userEmail
      }, { merge: true });

      setStatus('success');

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (error) {
      console.error('Error redeeming gift:', error);
      setError(error.message);
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'validating':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Validating Gift Token</h2>
            <p className="text-gray-600">Please wait while we verify your gift token...</p>
          </div>
        );

      case 'invalid':
        return (
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Gift Token</h2>
            <p className="text-gray-600 mb-6">This gift token is not valid or has expired.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        );

      case 'already_redeemed':
        return (
          <div className="text-center">
            <div className="text-orange-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Token Already Used</h2>
            <p className="text-gray-600 mb-6">This gift token has already been redeemed by another user.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="text-gray-500 text-6xl mb-4">⏰</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Token Expired</h2>
            <p className="text-gray-600 mb-6">This gift token has expired and can no longer be redeemed.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        );

      case 'valid':
        return (
          <div>
            <div className="text-center mb-8">
              <div className="text-green-500 text-6xl mb-4">🎁</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Congratulations!</h2>
              <p className="text-gray-600">You have a valid Lifetime Plan gift token!</p>
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 rounded-lg p-4 mt-4">
                <div className="text-sm font-mono text-purple-800 break-all">{token}</div>
              </div>
            </div>

            <form onSubmit={handleRedemption} className="space-y-6">
              <div className="flex gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => setIsNewUser(true)}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    isNewUser 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  New Account
                </button>
                <button
                  type="button"
                  onClick={() => setIsNewUser(false)}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    !isNewUser 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Existing Account
                </button>
              </div>

              {isNewUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={isNewUser ? "Create a password" : "Enter your password"}
                  minLength="6"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-4 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
              >
                🎁 Redeem Lifetime Plan Gift
              </button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <div className="font-medium mb-2">🎊 What you're getting:</div>
                <ul className="space-y-1 ml-4">
                  <li>• ✨ Lifetime access to Market Genie</li>
                  <li>• 🤖 Advanced AI analysis and insights</li>
                  <li>• 📊 Comprehensive analytics dashboard</li>
                  <li>• 🏷️ White-label branding options</li>
                  <li>• 🚀 API access for integrations</li>
                  <li>• 💎 Priority customer support</li>
                  <li>• 📈 Unlimited lead generation</li>
                </ul>
              </div>
            </form>
          </div>
        );

      case 'redeeming':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Redeeming Your Gift</h2>
            <p className="text-gray-600">Setting up your lifetime plan account...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Market Genie!</h2>
            <p className="text-gray-600 mb-4">Your lifetime plan has been activated successfully!</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800 mb-6">
              <div className="font-medium mb-2">🚀 Your account includes:</div>
              <ul className="space-y-1 ml-4 text-left">
                <li>• Lifetime access to all features</li>
                <li>• No monthly fees ever</li>
                <li>• Priority support</li>
                <li>• White-label capabilities</li>
              </ul>
            </div>
            <p className="text-gray-500 text-sm">Redirecting to your dashboard in a few seconds...</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h2>
            <p className="text-gray-600 mb-4">There was an error redeeming your gift token.</p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm mb-4">
                {error}
              </div>
            )}
            <button
              onClick={() => setStatus('valid')}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors mr-4"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        );

      default:
        return <div>Loading...</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {renderContent()}
      </div>
    </div>
  );
};

export default GiftRedemption;